import * as moment from 'moment';
import * as numeral from 'numeral';
import * as _ from 'underscore';
import * as Highcharts from 'highcharts';
import { AfterViewInit, Component, Input, OnDestroy, OnInit } from '@angular/core';

import { EmitterService } from '../../../../core/utility/services/emitter.service';
import { UtilityService } from '../../../../core/utility/services/utility.service';
import { DataTableConfig } from '../../../analytics-core/interfaces/widget.interface';
import { ColorUtilityService } from '../../../analytics-core/services/color.utility.service';
import { CONSTANTS, EVENT_EMITTERS } from '../../../analytics/constants/analytics.constants';
import { AnalyticsDataModel } from '../../../analytics/models/analytics.datamodel';
import { AnalyticsService } from '../../../analytics/services/analytics.service';
import { WidgetCore } from '../../interfaces/widget.core.interface';
import { DeviceCarouselV1Component } from '../device-donut-carousel-v1/device-donut-carousel-v1.component';

@Component({
	selector: 'app-devices-card-v3',
	styleUrls: ['./devices-card-v3.component.css'],
	templateUrl: './devices-card-v3.component.html'
})
export class DevicesCardV3Component
	extends DeviceCarouselV1Component
	implements OnInit, AfterViewInit, WidgetCore, OnDestroy
{
	public sectiontitle: string;
	public key: string;
	public colors: string;
	public groupby: string;
	public downloadCSVColumns: any;
	@Input() downloadcsvcolumns: any;
	@Input() downloadconfig: string;
	@Input() datatableconfig;
	@Input() sectionclass: string;
	public devicedatatableconfig: DataTableConfig = {} as DataTableConfig;
	public impressionsData: any = {};
	public clicksData: any = {};
	public chartArrData;
	public customLegendData: any;
	public enableCustomLegend = false;
	public seriesData: any[] = [];
	public reorderable = false;
	public totalObj: any = {};
	public dataTableObj: any = { data: [], columns: [] };
	public tooltipOptions = {
		animation: 'fade',
		arrow: true,
		duration: 750,
		dynamicTitle: true,
		position: 'top',
		scale: 'big'
	};
	public reloadSubscription;
	public widgetDataSubscription;
	public chartConfigObj = {
		addSeriesNameToLables: true,
		customLegendData: undefined,
		dataLabelsDistance: 5,
		datalabelsEnabled: false,
		enableCustomLegend: true,
		innerSize: '80',
		isTooltipEnabled: undefined,
		legendItemDistance: 100,
		legendText: '',
		lineBreak: undefined,
		margin: [],
		seriesName: '',
		size: 300,
		type: undefined,
		verticalAlign: '',
		xLegendPos: 0,
		yLegendPos: 0
	};
	public youtubeImpressions: number;
	private xAxisChartTitles: string[] = [];

	constructor(
		protected analyticsService: AnalyticsService,
		protected colorUtilityService: ColorUtilityService,
		protected analyticsDataModel: AnalyticsDataModel
	) {
		super(analyticsService, colorUtilityService, analyticsDataModel);
		this.reloadSubscription = EmitterService.get(EVENT_EMITTERS.RELOAD).subscribe((response: any) => {
			this.requestData();
			this.requestDeviceDatatableData();
		});
	}

	ngOnInit(): void {
		this.devicedatatableconfig = JSON.parse(decodeURI(this.datatableconfig));
		this.devicedatatableconfig.columns = _.filter(this.devicedatatableconfig.columns, (item) => {
			return this.analyticsDataModel.checkComponentForPermission(item);
		});
		this.fields = JSON.parse(decodeURI(this.fields));
		this.colorsconfig = JSON.parse(decodeURI(this.colorsconfig));
		this.downloadConfiguration = JSON.parse(decodeURI(this.downloadconfig));
		this.colorPalette = this.analyticsDataModel.partner.colorPalette;
		this.downloadCSVColumns = this.downloadcsvcolumns ? JSON.parse(decodeURI(this.downloadcsvcolumns)) : [];
		this.requestData();
	}

	ngAfterViewInit(): void {
		this.requestDeviceDatatableData();
	}

	ngOnDestroy(): void {
		if (this.reloadSubscription) {
			this.reloadSubscription.unsubscribe();
		}
		if (this.widgetDataSubscription) {
			this.widgetDataSubscription.unsubscribe();
		}
	}

	requestDeviceDatatableData(): void {
		this.showLoad = true;
		this.hasData = true;
		this.widgetDataSubscription = this.analyticsService.processWidgetRequest(this.key).subscribe((response: any) => {
			this.showLoad = false;
			if (response.success && response.data.length > 0) {
				this.youtubeImpressions = response.data.reduce((acc, item) => {
					return acc + parseInt(item.youtube_imprs);
				}, 0);
				this.dataTableObj.columns = this.generateColumnConfigurationForDataTable();
				this.generateDateTableConfiguration(response.data);
				this.processTableResponseData(response.data);
			} else {
				this.hasData = false;
				this.seriesData = [];
			}
		});
	}

	processTableResponseData(responseData: any): void {
		const chartData: any[] = [];
		const xAxisTitles: string[] = [];
		for (const data of responseData) {
			xAxisTitles.push(data.device);
		}
		this.xAxisChartTitles = xAxisTitles;
		this.seriesData = chartData;
		this.addFooterToTableData();
	}

	comparator(propertyName: any, a: any, b: any, c: any, d: any): number {
		if (parseFloat(c[propertyName]) < parseFloat(d[propertyName])) {
			return 1;
		}
		if (parseFloat(c[propertyName]) > parseFloat(d[propertyName])) {
			return -1;
		}
	}

	generateDateTableConfiguration(responseData: any): void {
		this.dataTableObj.data = this.generateResponseDataFromConfig(responseData);
	}

	sortData(column: any): void {
		const columnAndDataMap = {
			'Avg. Position': 'avg_position',
			CTR: 'clickthroughrate',
			'Click Rate': 'clickthroughrate',
			Clicks: 'clicks',
			Costs: 'cost',
			Device: 'device',
			Impressions: 'impressions',
			VTR: 'viewthroughrate',
			'View Throughs': 'view_throughs',
			'Household Site Visits': 'household_visits',
			'Website Attributions': 'website_attributions'
		};
		const lastRow = this.dataTableObj.data.pop();
		if (!lastRow[column] || lastRow[column] === 0) {
			lastRow[column] = 1;
		} else {
			lastRow[column] = 0;
		}
		this.dataTableObj.data = _.sortBy(this.dataTableObj.data, columnAndDataMap[column]);
		this.dataTableObj.data = lastRow[column] === 0 ? this.dataTableObj.data.reverse() : this.dataTableObj.data;
		this.dataTableObj.data.push(lastRow);
	}

	generateResponseDataFromConfig(responseData: any): any[] {
		const processedData = [];
		for (const responseObj of responseData) {
			processedData.push(this.getDerivedResponseObj(responseObj));
		}
		return processedData;
	}

	getDerivedResponseObj(responseObj: any): any {
		const columnObj: any = {};
		const columnsConfig = this.devicedatatableconfig.columns;
		for (const columnConfig of columnsConfig) {
			if (columnConfig.columnType === 'number') {
				const value = parseFloat(responseObj[columnConfig.columnKey]);
				columnObj[columnConfig.columnKey] = responseObj[columnConfig.columnKey] != null ? value : 0;
				columnObj['formatted_' + columnConfig.columnKey] =
					responseObj[columnConfig.columnKey] != null
						? this.isFloat(value)
							? numeral(value).format('0,0.00')
							: numeral(value).format('0,0')
						: '--';
				if (columnConfig.columnPrepend.length > 0 && columnObj['formatted_' + columnConfig.columnKey] !== '--') {
					columnObj['formatted_' + columnConfig.columnKey] =
						columnObj['formatted_' + columnConfig.columnKey] + columnConfig.columnPrepend;
				}
			} else if (columnConfig.columnType === 'date') {
				columnObj[columnConfig.columnKey] = moment(responseObj[columnConfig.columnKey]).format(
					columnConfig.columnFormat
				);
			} else {
				columnObj[columnConfig.columnKey] = responseObj[columnConfig.columnKey];
			}
		}
		columnObj['description'] = responseObj['description'];
		columnObj['tooltip'] = responseObj['tooltip'];
		return columnObj;
	}

	buildDeviceChartCollatorByFields(deviceData: any): any[] {
		const fieldChartArray: any[] = [];
		for (const field of this.fields) {
			const chartObjArray: any[] = [];
			const chartData: any = {};
			for (let i = 0; i < deviceData.length; i++) {
				const chartObj: any = {};
				chartObj.name = `${deviceData[i].device} ${UtilityService.upperCaseFirst(
					field == 'household_visits'
						? 'Household Site Visits'
						: field == 'website_attributions'
						? 'Website Attributions'
						: field
				)}`;
				chartObj.color = this.getColorFromColorConfig(i, 1);
				chartObj.y = deviceData[i][field] == 0 ? null : deviceData[i][field];
				chartObjArray.push(chartObj);
			}
			chartData.chartObj = chartObjArray;
			chartData.seriesName = field;
			chartData.type = 'pieDonut';
			chartData.verticalAlign = 'bottom';
			chartData.xLegendPos = 0;
			chartData.yLegendPos = 15;
			// chartData.height = 440;
			chartData.isLineBreakLabel = true;
			chartData.dataLabelsDistance = 15;
			chartData.margin = [4, 4, 0, 4];
			chartData.isTooltipEnabled = true;
			fieldChartArray.push(chartData);
		}
		this.impressionsData = fieldChartArray[0];
		this.clicksData = fieldChartArray[1];
		const customLegendData = this.buildCustomLegendArray(deviceData);
		this.chartConfigObj = {
			...this.chartConfigObj,
			customLegendData,
			isTooltipEnabled: this.impressionsData.isTooltipEnabled,
			legendText: this.impressionsData.legendText,
			lineBreak: this.impressionsData.isLineBreakLabel,
			margin: this.impressionsData.margin,
			seriesName: UtilityService.isUndefinedOrNull(this.clicksData)
				? this.impressionsData.seriesName
				: this.clicksData.seriesName,
			type: this.impressionsData.type,
			verticalAlign: this.impressionsData.verticalAlign,
			xLegendPos: this.impressionsData.xLegendPos,
			yLegendPos: this.impressionsData.yLegendPos
		};
		if (UtilityService.isUndefinedOrNull(this.clicksData)) {
			this.chartArrData = [
				{
					data: this.impressionsData.chartObj,
					dataLabels: {
						distance: 20
					},
					name: 'Devices',
					slicedOffset: 4,
					type: 'pie'
				}
			];
		} else {
			this.chartArrData = [
				{
					data: this.impressionsData.chartObj,
					dataLabels: {
						distance: 20
					},
					name: 'Devices',
					slicedOffset: 4,
					type: 'pie'
				},
				{
					allowPointSelect: true,
					data: this.clicksData.chartObj,
					dataLabels: {
						distance: -40
					},
					innerSize: '0%',
					name: 'Devices',
					size: '50%',
					slicedOffset: 4,
					type: 'pie'
				}
			];
		}
		return fieldChartArray;
	}

	buildCustomLegendArray(deviceData: any): any[] {
		const legendObjArray = [];
		let tempFields = JSON.parse(JSON.stringify(this.fields));
		for (let i = 0; i < deviceData.length; i++) {
			const tempObj: any = {};
			// tslint:disable-next-line: prefer-template
			var index = tempFields.indexOf('household_visits');
			if (index !== -1) {
				tempFields[index] = 'Household Site Visits';
			} else if (tempFields.indexOf('website_attributions') !== -1) {
				index = tempFields.indexOf('website_attributions');
				tempFields[index] = 'Website Attributions';
			}
			tempObj.name = deviceData[i].device + ' ' + tempFields.join('/');
			tempObj.color = this.getColorFromColorConfig(i, 1);
			tempObj.index = i;
			tempObj.active = true;
			legendObjArray.push(tempObj);
		}
		return legendObjArray;
	}

	downloadCSVData(e: any): void {
		e.preventDefault();
		this.analyticsService.downloadCSV(
			this.downloadConfiguration.csvQueryKey ?? this.key,
			this.devicedatatableconfig.downloadFileName,
			this.downloadCSVColumns
		);
	}

	filterItemsOfType(type: any): any[] {
		return this.devicedatatableconfig.columns.filter((x) => x.columnKey !== type);
	}

	protected isFloat(n: any): boolean {
		return Number(n) === n && n % 1 !== 0;
	}

	protected isInteger(n: any): boolean {
		return Number(n) === n && n % 1 === 0;
	}

	private addFooterToTableData(): void {
		const columns = this.devicedatatableconfig.columns;
		const data = this.dataTableObj.data;
		const clicksSum = this.calculateSumFromArray(_.pluck(data, 'clicks'));
		const impressionsSum = this.calculateSumFromArray(_.pluck(data, 'impressions'));
		const householdVisitsSum = this.calculateSumFromArray(_.pluck(data, 'household_visits'));
		const websiteAttributionsSum = this.calculateSumFromArray(_.pluck(data, 'website_attributions'));
		let clickRateSum = (clicksSum / impressionsSum) * 100;
		clickRateSum = parseFloat(clickRateSum.toFixed(2));
		const viewThroughsArray = _.pluck(data, 'view_throughs');
		const viewThroughsSum = this.calculateSumFromArray(viewThroughsArray);
		const costSum = this.calculateSumFromArray(_.pluck(data, 'cost'));
		const youtubeViewsSum = this.calculateSumFromArray(_.pluck(data, 'youtube_verified_imprs'));
		const avg_position_array = data.map((field) => {
			return Number((field.impressions * field.avg_position).toFixed(2));
		});
		const avg_position_sum = this.calculateSumFromArray(avg_position_array);
		const topImpressionSum = this.calculateSumFromArray(
			data.map((field) => {
				return Number((field.impressions * field.top_impression_share).toFixed(2));
			})
		);
		const absTopImpressionSum = this.calculateSumFromArray(
			data.map((field) => {
				return Number((field.impressions * field.abs_top_impression_share).toFixed(2));
			})
		);
		const absTopImpressionAvg = absTopImpressionSum / impressionsSum;
		const topImpressionAvg = topImpressionSum / impressionsSum;

		const avgPositionSum = avg_position_sum / impressionsSum;
		this.totalObj = {};
		for (const col of columns) {
			switch (col.columnKey) {
				case 'impressions': {
					this.totalObj['impressions'] = this.isFloat(impressionsSum)
						? numeral(impressionsSum).format('0,0.00')
						: numeral(impressionsSum).format('0,0');
					break;
				}
				case 'household_visits': {
					this.totalObj['household_visits'] = this.isFloat(householdVisitsSum)
						? numeral(householdVisitsSum).format('0,0.00')
						: numeral(householdVisitsSum).format('0,0');
					break;
				}
				case 'website_attributions': {
					this.totalObj['website_attributions'] = this.isFloat(websiteAttributionsSum)
						? numeral(websiteAttributionsSum).format('0,0.00')
						: numeral(websiteAttributionsSum).format('0,0');
					break;
				}
				case 'clicks': {
					this.totalObj['clicks'] = this.isFloat(clicksSum)
						? numeral(clicksSum).format('0,0.00')
						: numeral(clicksSum).format('0,0');
					break;
				}
				case 'clickthroughrate': {
					this.totalObj['clickthroughrate'] = this.isFloat(clickRateSum)
						? numeral(clickRateSum).format('0,0.00') + '%'
						: numeral(clickRateSum).format('0,0') + '%';
					break;
				}
				case 'view_throughs': {
					this.totalObj['view_throughs'] = this.isFloat(viewThroughsSum)
						? numeral(viewThroughsSum).format('0,0.00')
						: numeral(viewThroughsSum).format('0,0');
					break;
				}
				case 'viewthroughrate': {
					const viewThroughRateSum = (viewThroughsSum / impressionsSum) * 100;
					const viewThroughRate = parseFloat(viewThroughRateSum.toFixed(2));
					this.totalObj['viewthroughrate'] = this.isFloat(viewThroughRate)
						? numeral(viewThroughRate).format('0,0.00') + '%'
						: numeral(viewThroughRate).format('0,0') + '%';
					break;
				}
				case 'cost': {
					this.totalObj['cost'] = this.isFloat(costSum)
						? numeral(costSum).format('0,0.00') + '%'
						: numeral(costSum).format('0,0') + '%';
					break;
				}
				case 'avg_position': {
					this.totalObj['avg_position'] = this.isFloat(avgPositionSum)
						? numeral(avgPositionSum).format('0,0.00')
						: numeral(avgPositionSum).format('0,0');
					break;
				}
				case 'top_impression_share': {
					this.totalObj['top_impression_share'] = this.isFloat(topImpressionAvg)
						? numeral(topImpressionAvg).format('0,0.00') + '%'
						: numeral(topImpressionAvg).format('0,0') + '%';
					break;
				}
				case 'abs_top_impression_share': {
					this.totalObj['abs_top_impression_share'] = this.isFloat(absTopImpressionAvg)
						? numeral(absTopImpressionAvg).format('0,0.00') + '%'
						: numeral(absTopImpressionAvg).format('0,0') + '%';
					break;
				}
				case 'youtube_verified_imprs': {
					this.totalObj['youtube_verified_imprs'] = this.isFloat(youtubeViewsSum)
						? numeral(youtubeViewsSum).format('0,0.00')
						: numeral(youtubeViewsSum).format('0,0');
					break;
				}
				case 'youtube_verified_imprs_rate': {
					const youtubeViewsRateSum = (youtubeViewsSum / this.youtubeImpressions) * 100;
					const yotubeViewsRate = parseFloat(youtubeViewsRateSum.toFixed(2));
					this.totalObj['youtube_verified_imprs_rate'] = this.isFloat(yotubeViewsRate)
						? numeral(yotubeViewsRate).format('0,0.00') + '%'
						: numeral(yotubeViewsRate).format('0,0') + '%';
					break;
				}
			}
		}
		const obj = {
			device: '<b>Total</b>',
			formatted_abs_top_impression_share: `<b>${this.totalObj.abs_top_impression_share}</b>`,
			formatted_avg_position: `<b>${this.totalObj.avg_position}</b>`,
			formatted_clicks: `<b>${this.totalObj.clicks}</b>`,
			formatted_clickthroughrate: `<b>${this.totalObj.clickthroughrate}</b>`,
			formatted_cost: `<b>${this.totalObj.cost}</b>`,
			formatted_impressions: `<b>${this.totalObj.impressions}</b>`,
			formatted_top_impression_share: `<b>${this.totalObj.top_impression_share}</b>`,
			formatted_view_throughs: `<b>${this.totalObj.view_throughs}</b>`,
			formatted_viewthroughrate: `<b>${this.totalObj.viewthroughrate}</b>`,
			formatted_household_visits: `<b>${this.totalObj.household_visits}</b>`,
			formatted_website_attributions: `<b>${this.totalObj.website_attributions}</b>`,
			formatted_youtube_verified_imprs: `<b>${this.totalObj.youtube_verified_imprs}</b>`,
			formatted_youtube_verified_imprs_rate: `<b>${this.totalObj.youtube_verified_imprs_rate}</b>`
		};
		this.dataTableObj.data.push(obj);
	}

	private calculateAverageFromArray(array: number[]): number {
		const sum = _.reduce(
			array,
			(memo, num) => {
				return memo + num;
			},
			0
		);
		return sum / array.length;
	}

	private calculateSumFromArray(array: number[]): number {
		return _.reduce(
			array,
			(memo, num) => {
				return memo + num;
			},
			0
		);
	}

	private generateColumnConfigurationForDataTable(): any[] {
		const dateSet = {
			depricatedDate: this.analyticsDataModel.depricatedDate,
			endDate: this.analyticsDataModel.endDate,
			goLiveDate: this.analyticsDataModel.goLiveDate,
			startDate: this.analyticsDataModel.startDate
		};
		const columnsConfig = UtilityService.checkForVisibilityPermissions(this.devicedatatableconfig.columns, dateSet);
		const sortedColumnConfig = _.sortBy(columnsConfig, 'columnOrder');
		const columns = [];
		for (const colConfig of sortedColumnConfig) {
			const colObj: any = {} as any;
			colObj.name = colConfig.columnName;
			colObj.prop = colConfig.columnType === 'number' ? 'formatted_' + colConfig.columnKey : colConfig.columnKey;
			colObj.headerClass = colConfig.columnHeaderClass;
			colObj.cellClass = colConfig.columnCellClass;
			colObj.hasProgressBar = colConfig.columnHasProgressBar;
			colObj.columnKey = colConfig.columnKey;
			if (colConfig.columnType === 'number') {
				colObj.comparator = this.comparator.bind(this, colConfig.columnKey);
			}
			colObj.resizeable = false;
			colObj.columnHasTooltip = colConfig.columnHasTooltip;
			colObj.columnTooltip = colConfig.columnTooltip;
			columns.push(colObj);
		}
		return columns;
	}
}
